/**
 * Multi-language routing, slug mapping, and SEO hreflang configuration
 * Primary / Default language: Danish (da) without prefix
 * Secondary language: English (en) under /en/ prefix
 */

export const DEFAULT_LANG = 'da'
export const SUPPORTED_LANGS = ['da', 'en']

export const BASE_SITE_URL = 'https://mroutransport.dk'

// Canonical route paths per language
export const ROUTES = {
  home: {
    da: '/',
    en: '/en',
  },
  services: {
    da: '/transport',
    en: '/en/services',
  },
  about: {
    da: '/om-mrou-transport',
    en: '/en/about-us',
  },
  gallery: {
    da: '/galleri',
    en: '/en/gallery',
  },
  contact: {
    da: '/kontakt',
    en: '/en/contact',
  },
  forbidden: {
    da: '/403',
    en: '/en/403',
  },
}

// Canonical service keys and their localized slugs
export const SERVICE_SLUGS = {
  specialtransport: {
    da: 'specialtransport',
    en: 'specialized-transport',
  },
  containerkoersel: {
    da: 'containerkoersel',
    en: 'container-transport',
  },
  stykgods: {
    da: 'stykgods',
    en: 'general-cargo-transport',
  },
  stoebegods: {
    da: 'stykgods',
    en: 'general-cargo-transport',
  },
  traepiller: {
    da: 'traepiller',
    en: 'wood-pellet-transport',
  },
  'national-transport': {
    da: 'national-transport',
    en: 'domestic-transport',
  },
  'international-transport': {
    da: 'international-transport',
    en: 'international-transport',
  },
}

// Map any known slug back to canonical internal key
const SLUG_TO_KEY_MAP = {
  // Danish slugs
  specialtransport: 'specialtransport',
  containerkoersel: 'containerkoersel',
  stykgods: 'stykgods',
  stoebegods: 'stykgods',
  traepiller: 'traepiller',
  'national-transport': 'national-transport',
  'international-transport': 'international-transport',

  // English slugs
  'specialized-transport': 'specialtransport',
  'container-transport': 'containerkoersel',
  'general-cargo-transport': 'stykgods',
  'wood-pellet-transport': 'traepiller',
  'domestic-transport': 'national-transport',
}

/**
 * Resolve any incoming slug (DA or EN) to the internal canonical service key
 */
export function getCanonicalServiceKey(slug) {
  if (!slug) return 'specialtransport'
  const normalized = slug.toLowerCase().trim()
  return SLUG_TO_KEY_MAP[normalized] || 'specialtransport'
}

/**
 * Get localized slug for a given service key and language
 */
export function getLocalizedServiceSlug(serviceKey, lang = 'da') {
  const canonical = getCanonicalServiceKey(serviceKey)
  const mapping = SERVICE_SLUGS[canonical]
  if (!mapping) return canonical
  return lang === 'en' ? mapping.en : mapping.da
}

/**
 * Get localized URL path for a service detail page
 */
export function getServiceUrl(serviceKey, lang = 'da') {
  const slug = getLocalizedServiceSlug(serviceKey, lang)
  return lang === 'en' ? `/en/services/${slug}` : `/transport/${slug}`
}

/**
 * Get localized general page path
 */
export function getPageUrl(pageKey, lang = 'da') {
  const route = ROUTES[pageKey]
  if (!route) return lang === 'en' ? '/en' : '/'
  return lang === 'en' ? route.en : route.da
}

/**
 * Detect language from current pathname
 */
export function getLanguageFromPathname(pathname = '/') {
  if (pathname === '/en' || pathname.startsWith('/en/')) {
    return 'en'
  }
  return 'da'
}

/**
 * Calculate the corresponding URL path on another language for the current path
 */
export function getEquivalentPath(pathname = '/', targetLang = 'da') {
  const cleanPath = pathname.split('?')[0].split('#')[0] || '/'

  // 1. Home
  if (cleanPath === '/' || cleanPath === '/en' || cleanPath === '/en/') {
    return targetLang === 'en' ? '/en' : '/'
  }

  // 2. Services Overview
  if (
    cleanPath === '/transport' ||
    cleanPath === '/ydelser' ||
    cleanPath === '/en/services' ||
    cleanPath === '/en/transport'
  ) {
    return targetLang === 'en' ? '/en/services' : '/transport'
  }

  // 3. Service Detail
  const daServiceMatch = cleanPath.match(/^\/(?:transport|ydelser)\/([^/]+)/)
  const enServiceMatch = cleanPath.match(/^\/en\/(?:services|transport)\/([^/]+)/)
  if (daServiceMatch || enServiceMatch) {
    const rawSlug = daServiceMatch ? daServiceMatch[1] : enServiceMatch[1]
    const key = getCanonicalServiceKey(rawSlug)
    return getServiceUrl(key, targetLang)
  }

  // 4. About Us
  if (
    cleanPath === '/om-mrou-transport' ||
    cleanPath === '/om-os' ||
    cleanPath === '/en/about-us' ||
    cleanPath === '/en/about' ||
    cleanPath === '/en/om-mrou-transport' ||
    cleanPath === '/en/om-os'
  ) {
    return targetLang === 'en' ? '/en/about-us' : '/om-mrou-transport'
  }

  // 5. Gallery
  if (cleanPath === '/galleri' || cleanPath === '/en/gallery' || cleanPath === '/en/galleri') {
    return targetLang === 'en' ? '/en/gallery' : '/galleri'
  }

  // 6. Contact
  if (cleanPath === '/kontakt' || cleanPath === '/en/contact' || cleanPath === '/en/kontakt') {
    return targetLang === 'en' ? '/en/contact' : '/kontakt'
  }

  // 7. 403 Forbidden
  if (cleanPath === '/403' || cleanPath === '/en/403') {
    return targetLang === 'en' ? '/en/403' : '/403'
  }

  // Fallback for unknown / 404
  return targetLang === 'en' ? '/en' : '/'
}

/**
 * Get SEO Alternate Hreflang URLs and Canonical URL for current pathname
 */
export function getHreflangAndCanonical(pathname = '/', origin) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : BASE_SITE_URL)
  const daPath = getEquivalentPath(pathname, 'da')
  const enPath = getEquivalentPath(pathname, 'en')

  const currentLang = getLanguageFromPathname(pathname)
  const canonicalPath = currentLang === 'en' ? enPath : daPath

  return {
    currentLang,
    canonicalUrl: `${base}${canonicalPath}`,
    hreflangs: [
      { lang: 'da', url: `${base}${daPath}` },
      { lang: 'en', url: `${base}${enPath}` },
      { lang: 'x-default', url: `${base}${daPath}` }, // Danish is primary default
    ],
  }
}
